import { useCallback, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { OIDCProviderScope } from '../../../../../shared/model-typings/oidcprovider';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import type { OIDCProvider } from '../../../../../shared/model-typings/oidcprovider';

import ActionMenuDropdown from './ActionMenuDropdown';

type SortingRule = {
  id: string;
  desc: boolean;
};

const DEFAULT_SORT_MODE: SortingRule = { id: 'id', desc: false };

type OIDCProviderTableProps = {
  providers: OIDCProvider[];
  isLoading: boolean;
  onDeleteProvider: (id: number) => void;
  onViewProvider: (id: number) => void;
  shouldShowScope?: boolean;
};

const OIDCProviderTable: VoidFunctionComponent<OIDCProviderTableProps> = ({
  providers,
  isLoading,
  onDeleteProvider,
  onViewProvider,
  shouldShowScope = true,
}) => {
  const [sortMode, setSortMode] = useState<SortingRule>(DEFAULT_SORT_MODE);

  const ActionsCell = useCallback(
    ({ original }: { original: OIDCProvider }) => (
      <ActionMenuDropdown
        provider={original}
        onDeleteProvider={onDeleteProvider}
        onViewProvider={onViewProvider}
      />
    ),
    [onDeleteProvider, onViewProvider],
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'id',
        Header: (
          <TableHeader title="ID" sort={{ id: 'id', currentMode: sortMode }} />
        ),
        accessor: 'id',
        width: 100,
        resizable: false,
      },
      {
        id: 'issuer',
        Header: (
          <TableHeader title="Issuer" sort={{ id: 'issuer', currentMode: sortMode }} />
        ),
        accessor: 'issuer',
        style: {
          textOverflow: 'wrap',
        },
        resizable: false,
      },
      {
        id: 'scope',
        Header: (
          <TableHeader title="Scope" sort={{ id: 'scope', currentMode: sortMode }} />
        ),
        accessor: ({ scope }: OIDCProvider) => scope === OIDCProviderScope.ORG ? 'Organization' : 'Team',
        width: 100,
        resizable: false,
      },
    ];

    const scopeColumns = shouldShowScope ? [
      {
        id: 'teamName',
        Header: (
          <TableHeader title="Team Name" sort={{ id: 'teamName', currentMode: sortMode }} />
        ),
        accessor: 'teamName',
        width: 250,
        resizable: false,
      },
    ] : [];

    const actionsColumn = {
      id: 'actions',
      Header: <TableHeader title="Actions" />,
      resizable: false,
      width: 100,
      Cell: ActionsCell,
    };

    return [...baseColumns, ...scopeColumns, actionsColumn];
  }, [sortMode, ActionsCell, shouldShowScope]);

  return (
    <SimpleReactTable
      isSortable
      data={providers}
      columns={columns}
      defaultSortMode={[DEFAULT_SORT_MODE]}
      onSortedChanged={setSortMode}
      showPagination
      cacheId="oidcProviders"
      isLoading={isLoading}
    />
  );
};

export default OIDCProviderTable;
